import React, { memo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DealService } from '../../lib/types';
import { Calendar, DollarSign, LayoutList, Tag, GripVertical, FolderOpen, Eye, EyeOff } from 'lucide-react';
import { useDealStore } from '../../lib/stores/dealStore';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Create a mock terminal object if it's not available
const terminal = console;

interface DealServiceListProps {
  dealServices: DealService[];
  onEdit: (service: DealService) => void;
  onDelete: (id: string) => void;
  onReorder?: (reorderedServices: DealService[]) => void;
  dealStatus?: string;
  dealId?: string;
}

// Format Euro: forza Number() perche' i campi numeric Postgres arrivano
// spesso come string dal supabase-js → toLocaleString su stringa non separa
// le migliaia. Sempre it-IT (€1.200,00).
const formatEuro = (amount: number | string | null | undefined): string => {
  if (amount === null || amount === undefined || amount === '') return '€0,00';
  const n = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(n)) return '€0,00';
  return `€${n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// ---------------------------------------------------------------------------
// C2 — Metodo di pagamento + rate dettagliate per ciascun servizio dell'offerta
// ---------------------------------------------------------------------------

// Tipo minimo di una rata (compatibile con DealPaymentInstallment + varianti legacy)
type InstallmentLike = {
  type?: 'fixed' | 'percentage';
  value?: number;
  amount?: number;
  daysAfterStart?: number;
  description?: string;
  timeUnit?: 'days' | 'months';
  fixedDayOfMonth?: number;
  fixedDate?: string;
};

// Formatta una singola rata in italiano. Riusa la stessa logica di
// flusia-core/src/domains/documents/internal/processTemplate.ts (~1078-1116).
const formatInstallment = (inst: InstallmentLike): string => {
  let timeText = '';
  let timePrefix = '';

  if (inst.fixedDate) {
    const d = new Date(inst.fixedDate + 'T00:00:00');
    timeText = d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    timePrefix = 'entro il ';
  } else {
    const timeValue = inst.daysAfterStart ?? 0;
    const timeUnit = inst.timeUnit ?? 'days';
    const fixedDay = inst.fixedDayOfMonth;
    if (fixedDay) {
      timeText = `il giorno ${fixedDay} del mese`;
      timePrefix = '';
    } else if (timeUnit === 'months') {
      timeText = timeValue === 0 ? 'vista fattura' : timeValue === 1 ? '1 mese' : `${timeValue} mesi`;
      timePrefix = timeValue === 0 ? '' : 'dopo ';
    } else {
      timeText = timeValue === 0 ? 'vista fattura' : timeValue === 1 ? '1 giorno' : `${timeValue} giorni`;
      timePrefix = timeValue === 0 ? '' : 'dopo ';
    }
  }

  const desc = inst.description || 'Rata';
  if (inst.type === 'percentage') {
    return `${inst.value ?? 0}% ${timePrefix}${timeText} - ${desc}`.trim();
  }
  const amount = inst.amount ?? inst.value ?? 0;
  return `${formatEuro(amount)} ${timePrefix}${timeText} - ${desc}`.trim();
};

// Estrae le installments da una struttura payment_schedule che può essere
// un array oppure un oggetto { installments: [...] } (deal-level vs method-level).
const extractInstallments = (schedule: unknown): InstallmentLike[] => {
  if (!schedule) return [];
  if (Array.isArray(schedule)) return schedule as InstallmentLike[];
  const obj = schedule as { installments?: unknown };
  if (Array.isArray(obj.installments)) return obj.installments as InstallmentLike[];
  return [];
};

// Risolve metodo + rate per un service item. Preferisce lo schedule
// specifico del servizio (override), altrimenti quello del metodo template.
const getServicePaymentInfo = (
  service: DealService
): { methodName: string | null; installments: InstallmentLike[] } => {
  const methodName = service.payment_method?.name || null;
  const serviceInstallments = extractInstallments(service.payment_schedule);
  const installments =
    serviceInstallments.length > 0
      ? serviceInstallments
      : extractInstallments(service.payment_method?.payment_schedule);
  return { methodName, installments };
};

// Blocco UI compatto Notion-like: metodo in grassetto + lista rate.
// Collassabile se ci sono molte rate (mostra le prime 3 + "+N").
const ServicePaymentInfo = memo(({ service }: { service: DealService }) => {
  const [expanded, setExpanded] = useState(false);
  const { methodName, installments } = getServicePaymentInfo(service);

  if (!methodName && installments.length === 0) return null;

  const COLLAPSED_COUNT = 3;
  const isCollapsible = installments.length > COLLAPSED_COUNT;
  const visible = expanded || !isCollapsible ? installments : installments.slice(0, COLLAPSED_COUNT);
  const hiddenCount = installments.length - COLLAPSED_COUNT;

  return (
    <div className="mt-1.5">
      {methodName && (
        <div className="text-xs font-medium text-[#37352F] dark:text-gray-200">{methodName}</div>
      )}
      {installments.length > 0 && (
        <ul className="mt-0.5 space-y-0.5">
          {visible.map((inst, i) => (
            <li key={i} className="text-[11px] leading-snug text-[#787774] dark:text-gray-400">
              • {formatInstallment(inst)}
            </li>
          ))}
          {isCollapsible && (
            <li>
              <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="text-[11px] text-[#787774] dark:text-gray-400 hover:underline"
              >
                {expanded ? 'Mostra meno' : `+${hiddenCount} rate`}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
});

// Helper function per ottenere il service_type corretto per servizi personalizzati
const getServiceType = (service: DealService): string | undefined => {
  // Un servizio è personalizzato se:
  // 1. service_id è null o undefined
  // 2. service_id inizia con "custom-"
  // 3. ha custom_service_name
  const isCustomService = !service.service_id ||
                         service.service_id?.toString().startsWith('custom-') ||
                         !!service.custom_service_name;
  return isCustomService
    ? (service.metadata?.custom_service_type || service.service?.service_type)
    : service.service?.service_type;
};

// Etichetta frequenza di fatturazione (metadata.billing_interval_months). L'importo resta mensile;
// questo indica solo ogni quanto si emette la fattura. null = mensile standard (nessuna nota).
const getBillingFrequencyLabel = (service: DealService): string | null => {
  const n = (service.metadata as any)?.billing_interval_months;
  if (n === 2) return 'Fatturazione bimestrale';
  if (n === 3) return 'Fatturazione trimestrale';
  if (n === 4) return 'Fatturazione quadrimestrale';
  if (n === 6) return 'Fatturazione semestrale';
  if (n === 12) return 'Fatturazione annuale';
  return null;
};

// Suffisso periodo del canone (l'importo è il canone del periodo). Default /mese.
const getBillingPeriodSuffix = (service: DealService): string => {
  const n = (service.metadata as any)?.billing_interval_months;
  if (n === 2) return '/bimestre';
  if (n === 3) return '/trimestre';
  if (n === 4) return '/quadrimestre';
  if (n === 6) return '/semestre';
  if (n === 12) return '/anno';
  return '/mese';
};

// Intervallo in mesi (per convertire il canone in equivalente mensile nei totali).
const getBillingIntervalMonths = (service: DealService): number => {
  const n = (service.metadata as any)?.billing_interval_months;
  return n && n > 0 ? n : 1;
};

// Servizio terminato: recurring_status cancelled/expired, oppure contract_expiry_date già passata.
// (coerente con isTerminated della griglia). Ritorna la motivazione se presente.
const getTerminationInfo = (service: DealService): { terminated: boolean; reason?: string } => {
  const status = service.recurring_status;
  const reason = (service.metadata as any)?.termination_reason || undefined;
  if (status === 'cancelled' || status === 'expired') return { terminated: true, reason };
  if (service.contract_expiry_date) {
    const exp = new Date(service.contract_expiry_date);
    if (!isNaN(exp.getTime()) && exp < new Date()) return { terminated: true, reason };
  }
  return { terminated: false };
};

// Componente per la riga trascinabile
const SortableServiceRow = memo(({ service, index, formatDescription, onEdit, onDelete, dealStatus, onViewProject }: { 
  service: DealService; 
  index: number;
  formatDescription: (desc: string | undefined | null) => string;
  onEdit: (service: DealService) => void;
  onDelete: (id: string) => void;
  dealStatus?: string;
  onViewProject?: (serviceId: string) => void;
}) => {
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: service.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="border-b border-[rgba(55,53,47,0.09)] dark:border-gray-800 last:border-b-0 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.04] transition-colors"
    >
      <td className="py-3 pl-2 pr-2 text-sm w-[2%]">
        <div {...attributes} {...listeners} className="cursor-move p-1 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 rounded">
          <GripVertical className="h-4 w-4 text-[#787774]/50 dark:text-white/30" />
        </div>
      </td>
      <td className="py-3 pl-2 pr-3 text-sm sm:pl-2 w-[38%]">
        <div className="font-medium text-[#37352F] dark:text-white flex items-center gap-2">
          {(() => {
            // VIVA: riga articolo
            if ((service as any).line_type === 'product') {
              return service.custom_service_name || 'Articolo';
            }

            const isCustomService = !service.service_id ||
                                   service.service_id?.toString().startsWith('custom-') ||
                                   !!service.custom_service_name;

            if (isCustomService) {
              return service.custom_service_name || 'Servizio personalizzato';
            }

            return service.service?.title || 'Servizio';
          })()}
        </div>
        {(service.description || service.service?.description) && (
          <div className="mt-1 text-xs text-[#787774] dark:text-gray-400">
            {formatDescription(service.description || service.service?.description)}
          </div>
        )}
        {/* C2: metodo di pagamento + rate dettagliate per servizio */}
        <ServicePaymentInfo service={service} />
      </td>
      <td className="px-3 py-3 text-sm w-[25%]">
        {(() => {
          // VIVA: riga articolo — badge dedicato + quantita/UM invece di Mensile/Annuale
          if ((service as any).line_type === 'product') {
            const qty = (service as any).quantity;
            const um = (service as any).unit_of_measure || 'pz';
            return (
              <div className="flex flex-col gap-1">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 w-fit">
                  Articolo
                </span>
                {qty != null && (
                  <span className="text-xs text-[#787774] dark:text-gray-400">
                    Qtà: {Number(qty).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 3 })} {um}
                  </span>
                )}
              </div>
            );
          }

          const serviceType = getServiceType(service);

          return (
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
              serviceType === 'monthly'
                ? 'bg-[rgba(35,131,226,0.1)] dark:bg-blue-900/30 text-[#2383E2] dark:text-blue-300'
                : serviceType === 'annual'
                ? 'bg-[rgba(15,123,108,0.1)] dark:bg-green-900/30 text-[#0F7B6C] dark:text-green-300'
                : 'text-[#787774] dark:text-gray-400'
            }`}>
              {serviceType === 'monthly' &&
                <Calendar className="h-3 w-3 mr-1" />
              }
              {serviceType === 'annual' &&
                <Calendar className="h-3 w-3 mr-1" />
              }
              {serviceType === 'monthly'
                ? 'Mensile'
                : serviceType === 'annual'
                ? 'Annuale'
                : 'Una Tantum'}
            </span>
          );
        })()}

        {getBillingFrequencyLabel(service) && (
          <div className="mt-1">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[rgba(203,145,47,0.12)] text-[#CB912F] dark:bg-amber-900/30 dark:text-amber-300">
              {getBillingFrequencyLabel(service)}
            </span>
          </div>
        )}
        {(() => {
          const term = getTerminationInfo(service);
          if (!term.terminated) return null;
          return (
            <div className="mt-1">
              <span
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                title={term.reason ? `Terminato: ${term.reason}` : 'Servizio terminato'}
              >
                🔴 Terminato
              </span>
            </div>
          );
        })()}

        {(() => {
          const serviceType = getServiceType(service);

          return serviceType === 'monthly' && service.monthly_duration && service.monthly_duration > 0 && (
            <div className="mt-1 text-xs text-black/60 dark:text-white/60 flex items-center">
              <Calendar className="h-3 w-3 mr-1" />
              Durata: {service.monthly_duration} mesi
            </div>
          );
        })()}
      </td>
      <td className="px-3 py-3 text-sm text-black/80 dark:text-white/80 w-[25%]">
        {(() => {
          const serviceType = getServiceType(service);
          return service.discounted_amount ? (
            <div className="flex flex-col">
              <div className="line-through text-black/40 dark:text-white/40">
                {formatEuro(service.amount)}
                {serviceType === 'monthly' && <span className="text-xs text-black/60 dark:text-white/60">{getBillingPeriodSuffix(service)}</span>}
                {serviceType === 'annual' && <span className="text-xs text-black/60 dark:text-white/60">/anno</span>}
              </div>
              <div className="text-green-600 dark:text-green-400 font-medium">
                {formatEuro(service.discounted_amount)}
                {serviceType === 'monthly' && (!service.monthly_duration || service.monthly_duration <= 0) && <span className="ml-1">{getBillingPeriodSuffix(service)}</span>}
                {serviceType === 'annual' && <span className="ml-1">/anno</span>}
              </div>

              {serviceType === 'monthly' && service.monthly_duration && service.monthly_duration > 0 && (
                <div className="text-xs mt-1 text-green-700 dark:text-green-300 font-medium">
                  {formatEuro(Number(service.discounted_amount) * Number(service.monthly_duration))} totale
                </div>
              )}
            </div>
          ) : (
            <div>
              <div>
                {formatEuro(service.amount)}
                {serviceType === 'monthly' && (!service.monthly_duration || service.monthly_duration <= 0) && <span className="ml-1">{getBillingPeriodSuffix(service)}</span>}
                {serviceType === 'annual' && <span className="ml-1">/anno</span>}
              </div>

              {serviceType === 'monthly' && service.monthly_duration && service.monthly_duration > 0 && (
                <div className="text-xs mt-1 text-black/70 dark:text-white/70 font-medium">
                  {formatEuro(Number(service.amount) * Number(service.monthly_duration))} totale
                </div>
              )}
            </div>
          );
        })()}
        
        {/* Cost Breakdown Preview */}
        {service.cost_breakdown && (service.cost_breakdown.costsWithMarkup > 0 || service.cost_breakdown.costsWithoutMarkup > 0) && (
          <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/30 rounded text-xs">
            <div className="flex justify-between items-center mb-1">
              <span className="font-medium text-blue-800 dark:text-blue-200">Costi aggiuntivi:</span>
              <button
                onClick={() => {/* Toggle cost details */}}
                className="text-blue-600 hover:text-blue-800"
              >
                <Eye className="h-3 w-3" />
              </button>
            </div>
            <div className="space-y-1 text-blue-700 dark:text-blue-300">
              <div className="flex justify-between">
                <span>Base:</span>
                <span>{formatEuro(service.cost_breakdown.totalBaseCost || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span>Markup:</span>
                <span>{formatEuro(service.cost_breakdown.totalMarkup || 0)}</span>
              </div>
              <div className="flex justify-between border-t border-blue-200 pt-1 font-medium">
                <span>Cliente:</span>
                <span>{formatEuro(service.cost_breakdown.totalClientPrice || 0)}</span>
              </div>
            </div>
          </div>
        )}
      </td>
      <td className="px-3 py-3 text-sm w-[10%]">
        <div className="flex items-center gap-1">
          {dealStatus === 'accepted' && onViewProject && (
            <button
              onClick={() => onViewProject(service.id)}
              className="p-1 hover:bg-gray-700/50 dark:hover:bg-gray-700 rounded transition-colors"
              title="Visualizza progetto"
            >
              <FolderOpen className="h-4 w-4 text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
});

export const DealServiceList = memo(function DealServiceList({ dealServices, onReorder, onEdit, onDelete, dealStatus, dealId }: DealServiceListProps) {
  const navigate = useNavigate();
  const { getDealCostBreakdown } = useDealStore();

  // Stato locale dei servizi per il drag and drop
  const [services, setServices] = useState<DealService[]>(dealServices);
  const [dealCostBreakdown, setDealCostBreakdown] = useState<any>(null);
  const [showTotalBreakdown, setShowTotalBreakdown] = useState(false);

  // Load deal cost breakdown
  useEffect(() => {
    if (dealId) {
      loadDealCostBreakdown();
    }
  }, [dealId, dealServices]);

  const loadDealCostBreakdown = async () => {
    if (!dealId) return;
    
    try {
      const breakdown = await getDealCostBreakdown(dealId);
      setDealCostBreakdown(breakdown);
    } catch (error) {
      console.error('Error loading deal cost breakdown:', error);
    }
  };
  
  // Funzione per navigare al progetto corrispondente (condivisa tra desktop e mobile)
  const handleViewProject = async (serviceId: string) => {
    if (!dealId || !serviceId) return;
    
    try {
      const { supabase } = await import('../../lib/supabase');

      // Il collegamento progetto→offerta esiste in due forme: la colonna
      // projects.deal_id, scritta dallo step workflow create_projects_from_services,
      // e metadata.deal_id, scritta dal percorso storico nel CRM. Cercando solo nei
      // metadata il pulsante non trovava i progetti creati dal workflow e non faceva
      // nulla, senza dire perché.
      const { data: byColumn, error } = await supabase
        .from('projects')
        .select('id, name')
        .eq('deal_id', dealId)
        .eq('metadata->>deal_service_id', serviceId);

      if (error) {
        console.error('Error finding project:', error);
        return;
      }

      let projects = byColumn;
      if (!projects || projects.length === 0) {
        const { data: legacy } = await supabase
          .from('projects')
          .select('id, name')
          .eq('metadata->>deal_id', dealId)
          .eq('metadata->>deal_service_id', serviceId);
        projects = legacy;
      }

      if (projects && projects.length > 0) {
        navigate(`/projects/${projects[0].id}`);
      } else {
        console.log('No project found for this service');
      }
    } catch (error) {
      console.error('Error navigating to project:', error);
    }
  };

  // Configurazione dei sensori per il drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Inizia a trascinare dopo 5px di movimento per evitare click accidentali
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Aggiorniamo i servizi quando cambia dealServices (props)
  useEffect(() => {
    // Impostiamo i servizi in modo ordinato per order
    const sortedServices = [...dealServices].sort((a, b) => 
      (a.order !== null && b.order !== null) ? a.order - b.order : 0
    );
    setServices(sortedServices);
  }, [dealServices]);

  // Gestione del completamento del drag
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Trova gli indici degli elementi trascinati
      const oldIndex = services.findIndex(service => service.id === active.id);
      const newIndex = services.findIndex(service => service.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        // Crea un nuovo array con l'elemento spostato
        const newServices = [...services];
        const [movedItem] = newServices.splice(oldIndex, 1);
        newServices.splice(newIndex, 0, movedItem);
        
        // Aggiorna lo stato locale
        setServices(newServices);
        
        // Chiamiamo il callback per aggiornare l'ordine nel componente padre
        if (onReorder) {
          // Assegniamo nuovi valori di 'order' ai servizi prima di passarli al callback
          const updatedServices = newServices.map((service, index) => ({
            ...service,
            order: index
          }));
          onReorder(updatedServices);
        }
      }
    }
  };

  // Calcola il totale di un servizio considerando la durata mensile se specificata
  const calculateServiceTotal = (service: DealService) => {
    const baseAmount = service.discounted_amount ?? service.amount;
    const serviceType = getServiceType(service);

    // Se è un servizio mensile e ha una durata specificata (> 0), moltiplica per il numero di mesi
    if (serviceType === 'monthly' && service.monthly_duration && service.monthly_duration > 0) {
      return baseAmount * service.monthly_duration;
    }

    // Altrimenti ritorna l'importo base
    return baseAmount;
  };

  // Calcola il totale generale
  const getTotalAmount = () => {
    return services.reduce((sum, service) => sum + calculateServiceTotal(service), 0);
  };

  // Totale mensile ricorrente: il canone è per periodo (es. trimestrale) → riportato a
  // equivalente mensile dividendo per l'intervallo, così la somma resta coerente "/mese".
  const getMonthlyTotal = () => {
    return services
      .filter(service => getServiceType(service) === 'monthly' && (!service.monthly_duration || service.monthly_duration <= 0))
      .reduce((sum, service) => sum + ((service.discounted_amount ?? service.amount) / getBillingIntervalMonths(service)), 0);
  };

  // Calcola il totale una tantum (inclusi i servizi mensili con durata fissa > 0)
  const getOnetimeTotal = () => {
    return services
      .filter(service => getServiceType(service) !== 'monthly' || (service.monthly_duration && service.monthly_duration > 0))
      .reduce((sum, service) => {
        const serviceType = getServiceType(service);
        // Se è un servizio mensile con durata > 0, calcola il totale per quel periodo
        if (serviceType === 'monthly' && service.monthly_duration && service.monthly_duration > 0) {
          return sum + ((service.discounted_amount ?? service.amount) * service.monthly_duration);
        }
        // Se è un servizio una tantum, aggiungi l'importo
        if (serviceType !== 'monthly') {
          return sum + (service.discounted_amount ?? service.amount);
        }
        return sum;
      }, 0);
  };
  
  // Verifica se ci sono servizi mensili
  const hasMonthlyServices = services.some(service => getServiceType(service) === 'monthly');

  // Verifica se ci sono servizi una tantum
  const hasOnetimeServices = services.some(service => getServiceType(service) !== 'monthly');

  const formatDescription = (description: string | undefined | null) => {
    if (!description) return '';
    
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = description;
    
    // Get text content without HTML tags
    const text = tempDiv.textContent || tempDiv.innerText || '';
    
    // Return first 100 characters with ellipsis if needed
    return text.length > 100 ? text.substring(0, 100) + '...' : text;
  };

  // Memoize the return value
  return (
    <div className="notion-table-fullwidth dark:rounded-xl dark:border dark:border-gray-700 dark:bg-gray-800/50">
      {/* Table for medium and large screens */}
      <div className="hidden md:block">
        <table className="min-w-full">
          <thead className="bg-[#FBFBFA] dark:bg-gray-800/90">
            <tr className="border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700">
              <th className="py-2 pl-2 pr-2 text-left text-[11px] font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wider w-[2%] bg-[#FBFBFA] dark:bg-gray-800/90">
                {/* Colonna per l'icona drag */}
              </th>
              <th className="py-2 pl-2 pr-3 text-left text-[11px] font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wider sm:pl-2 w-[38%] bg-[#FBFBFA] dark:bg-gray-800/90">
                Servizio
              </th>
              <th className="px-3 py-2 text-left text-[11px] font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wider w-[25%] bg-[#FBFBFA] dark:bg-gray-800/90">
                Tipo
              </th>
              <th className="px-3 py-2 text-left text-[11px] font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wider w-[25%] bg-[#FBFBFA] dark:bg-gray-800/90">
                Prezzo
              </th>
              <th className="px-3 py-2 text-left text-[11px] font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wider w-[10%] bg-[#FBFBFA] dark:bg-gray-800/90">
                Azioni
              </th>
            </tr>
          </thead>
          <tbody>
            {services.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-sm text-black/60 dark:text-white/60">
                  Nessun servizio aggiunto a questa trattativa.
                </td>
              </tr>
            ) : (
              <>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext 
                    items={services.map(service => service.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {services.map((service, index) => (
                      <SortableServiceRow
                        key={service.id}
                        service={service}
                        index={index}
                        formatDescription={formatDescription}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        dealStatus={dealStatus}
                        onViewProject={handleViewProject}
                      />
                    ))}
                  </SortableContext>
                </DndContext>

                {/* Total section - Notion style */}
                <tr className="border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700">
                  <td colSpan={5} className="px-0 py-0">
                    <div>
                      {/* Servizi mensili ricorrenti (senza durata definita o durata <= 0) */}
                      {hasMonthlyServices && services.some(s => getServiceType(s) === 'monthly' && (!s.monthly_duration || s.monthly_duration <= 0)) && (
                        <div className="bg-[#FBFBFA] dark:bg-gray-800/50 px-4 py-2 grid grid-cols-12 items-center border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700">
                          <div className="text-xs font-medium text-[#2383E2] dark:text-blue-300 col-span-8">
                            Totale mensile ricorrente
                          </div>
                          <div className="text-xs font-medium text-[#2383E2] dark:text-blue-300 text-right col-span-4">
                            {formatEuro(getMonthlyTotal())}/mese
                          </div>
                        </div>
                      )}

                      {/* Servizi una tantum o mensili con durata definita */}
                      {hasOnetimeServices && (
                        <div className="bg-[#FBFBFA] dark:bg-gray-800/50 px-4 py-2 grid grid-cols-12 items-center border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700">
                          <div className="text-xs font-medium text-[#787774] dark:text-gray-300 col-span-8">
                            Totale una tantum
                          </div>
                          <div className="text-xs font-medium text-[#37352F] dark:text-white text-right col-span-4">
                            {formatEuro(getOnetimeTotal())}
                          </div>
                        </div>
                      )}

                      {/* Totale generale */}
                      <div className="bg-[#FBFBFA] dark:bg-gray-800/70 px-4 py-2.5 grid grid-cols-12 items-center">
                        <div className="text-sm font-semibold text-[#37352F] dark:text-white col-span-8">
                          Totale servizi
                        </div>
                        <div className="text-sm font-semibold text-[#37352F] dark:text-white text-right col-span-4">
                          {formatEuro(getTotalAmount())}
                        </div>
                      </div>

                      {/* Cost Breakdown Summary */}
                      {dealCostBreakdown && dealCostBreakdown.totals && dealCostBreakdown.totals.costsClientTotal > 0 && (
                        <>
                          <div className="bg-blue-50 dark:bg-blue-900/20 px-6 py-3 grid grid-cols-12 items-center">
                            <div className="font-medium text-blue-800 dark:text-blue-300 flex items-center col-span-8">
                              <button
                                onClick={() => setShowTotalBreakdown(!showTotalBreakdown)}
                                className="flex items-center hover:underline"
                              >
                                {showTotalBreakdown ? <EyeOff className="h-4 w-4 mr-1.5" /> : <Eye className="h-4 w-4 mr-1.5" />}
                                Costi aggiuntivi
                              </button>
                            </div>
                            <div className="font-medium text-blue-800 dark:text-blue-300 text-right col-span-4">
                              {formatEuro(dealCostBreakdown.totals.costsClientTotal)}
                            </div>
                          </div>

                          {showTotalBreakdown && (
                            <div className="bg-blue-25 dark:bg-blue-900/10 px-6 py-3 border-l-4 border-blue-300 dark:border-blue-600">
                              <div className="space-y-2 text-sm">
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <span className="text-blue-700 dark:text-blue-400">Costi base:</span>
                                    <div className="font-medium">{formatEuro(dealCostBreakdown.totals.costsBaseTotalTotal || 0)}</div>
                                  </div>
                                  <div>
                                    <span className="text-blue-700 dark:text-blue-400">Markup:</span>
                                    <div className="font-medium">{formatEuro(dealCostBreakdown.totals.costsMarkupTotal || 0)}</div>
                                  </div>
                                </div>
                                
                                {dealCostBreakdown.services && dealCostBreakdown.services.length > 0 && (
                                  <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
                                    <div className="text-xs text-blue-600 dark:text-blue-400 mb-2">Breakdown per servizio:</div>
                                    <div className="space-y-1">
                                      {dealCostBreakdown.services.filter((s: any) => s.costBreakdown.totalClientPrice > 0).map((service: any, idx: number) => (
                                        <div key={idx} className="flex justify-between text-xs">
                                          <span className="truncate mr-2">{service.service?.title || service.custom_service_name || 'Servizio sconosciuto'}:</span>
                                          <span>{formatEuro(service.costBreakdown.totalClientPrice)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Grand Total */}
                          <div className="bg-green-50 dark:bg-green-900/20 px-6 py-3 grid grid-cols-12 items-center border-t-2 border-green-200 dark:border-green-700">
                            <div className="font-bold text-green-800 dark:text-green-300 flex items-center col-span-8">
                              <DollarSign className="h-4 w-4 mr-1.5" />
                              Totale Generale (con costi)
                            </div>
                            <div className="font-bold text-green-800 dark:text-green-300 text-right col-span-4">
                              {formatEuro(getTotalAmount() + dealCostBreakdown.totals.costsClientTotal)}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Card layout for small screens - Notion style */}
      <div className="md:hidden">
        {services.length === 0 ? (
          <div className="p-4 text-center text-sm text-[#787774] dark:text-gray-400">
            Nessun servizio aggiunto a questa trattativa.
          </div>
        ) : (
          <>
            <div className="px-4 py-2 bg-[#FBFBFA] dark:bg-gray-800/90 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wider">
                  Servizi ({services.length})
                </div>

                <div className="text-[11px] font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wider">
                  Prezzo
                </div>
              </div>
            </div>

            {services.map((service, index) => (
              <div
                key={service.id}
                className={`p-3.5 hover:bg-black/5 dark:hover:bg-white/5 ${index % 2 === 0 ? 'bg-black/[0.02] dark:bg-white/[0.02]' : ''}`}
              >
                <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
                  {/* Title and badge */}
                  <div className="flex-1">
                    <div className="flex flex-wrap items-start justify-between mb-1.5">
                      <h3 className="text-sm font-medium text-black dark:text-white mr-2">
                        {/* SOLUZIONE CORRETTA: stessa logica della vista desktop */}
                        {(() => {
                          // Verifica se è un servizio personalizzato in tre modi:
                          // 1. service_id è null o undefined
                          // 2. service_id inizia con "custom-"
                          // 3. ha custom_service_name
                          const isCustomService = !service.service_id ||
                                                 service.service_id?.toString().startsWith('custom-') ||
                                                 !!service.custom_service_name;

                          if (isCustomService) {
                            return service.custom_service_name || 'Servizio personalizzato';
                          }

                          // Altrimenti è un servizio normale del catalogo
                          return service.service?.title || 'Servizio';
                        })()}
                      </h3>

                      {(() => {
                        // Usa la funzione helper centralizzata
                        const serviceType = getServiceType(service);

                        return (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            serviceType === 'monthly'
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                              : serviceType === 'annual'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                              : 'glass-card text-black dark:text-white'
                          }`}>
                            {serviceType === 'monthly' &&
                              <Calendar className="h-3 w-3 mr-1 text-blue-600" />
                            }
                            {serviceType === 'annual' &&
                              <Calendar className="h-3 w-3 mr-1 text-green-600" />
                            }
                            {serviceType === 'monthly'
                              ? 'Mensile'
                              : serviceType === 'annual'
                              ? 'Annuale'
                              : 'Una Tantum'}
                          </span>
                        );
                      })()}
                      {getBillingFrequencyLabel(service) && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
                          {getBillingFrequencyLabel(service)}
                        </span>
                      )}
                      {(() => {
                        const term = getTerminationInfo(service);
                        if (!term.terminated) return null;
                        return (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                            title={term.reason ? `Terminato: ${term.reason}` : 'Servizio terminato'}
                          >
                            🔴 Terminato
                          </span>
                        );
                      })()}
                    </div>

                    {/* Description if available */}
                    {(service.description || service.service?.description) && (
                      <p className="text-xs text-black/70 dark:text-white/70">
                        {formatDescription(service.description || service.service?.description)}
                      </p>
                    )}

                    {/* Monthly duration if applicable */}
                    {getServiceType(service) === 'monthly' && service.monthly_duration && service.monthly_duration > 0 && (
                      <div className="mt-1 text-xs flex items-center text-black/60 dark:text-white/60">
                        <Calendar className="h-3 w-3 mr-1 text-black/50 dark:text-white/50" />
                        Durata: {service.monthly_duration} mesi
                      </div>
                    )}

                    {/* C2: metodo di pagamento + rate dettagliate per servizio */}
                    <ServicePaymentInfo service={service} />
                  </div>

                  {/* Price display */}
                  <div className="mt-1 sm:mt-0 flex flex-col items-end sm:text-right">
                    {(() => {
                      const serviceType = getServiceType(service);
                      return service.discounted_amount ? (
                        <>
                          <span className="line-through text-xs text-black/40 dark:text-white/40">
                            {formatEuro(service.amount)}
                            {serviceType === 'monthly' && <span className="text-xs text-black/60 dark:text-white/60">{getBillingPeriodSuffix(service)}</span>}
                          </span>
                          <span className="text-green-600 dark:text-green-400 font-medium">
                            {formatEuro(service.discounted_amount)}
                            {serviceType === 'monthly' && (!service.monthly_duration || service.monthly_duration <= 0) && <span className="ml-1 text-xs">{getBillingPeriodSuffix(service)}</span>}
                          </span>

                          {serviceType === 'monthly' && service.monthly_duration && service.monthly_duration > 0 && (
                            <span className="mt-1 text-xs text-green-700 dark:text-green-300 font-medium bg-green-50 dark:bg-green-900/30 px-2 py-0.5 rounded-md">
                              {formatEuro(Number(service.discounted_amount) * Number(service.monthly_duration))} totale
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          <span className="font-medium">
                            {formatEuro(service.amount)}
                            {serviceType === 'monthly' && (!service.monthly_duration || service.monthly_duration <= 0) && <span className="ml-1 text-xs">{getBillingPeriodSuffix(service)}</span>}
                          </span>

                          {serviceType === 'monthly' && service.monthly_duration && service.monthly_duration > 0 && (
                            <span className="mt-1 text-xs text-black/70 dark:text-white/70 font-medium bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-md">
                              {formatEuro(Number(service.amount) * Number(service.monthly_duration))} totale
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
                
                {/* Actions for mobile */}
                {dealStatus === 'accepted' && (
                  <div className="mt-2 flex justify-end">
                    <button
                      onClick={() => handleViewProject(service.id)}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-md hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                    >
                      <FolderOpen className="h-3 w-3" />
                      Vai al Progetto
                    </button>
                  </div>
                )}
              </div>
            ))}

            {/* Totals section with improved visuals and icons */}
            <div className="divide-y divide-black/10 dark:divide-white/10">
              {/* Monthly total for mobile - only for recurring services with no duration or duration <= 0 */}
              {hasMonthlyServices && dealServices.some(s => getServiceType(s) === 'monthly' && (!s.monthly_duration || s.monthly_duration <= 0)) && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 font-medium">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-blue-800 dark:text-blue-300 flex items-center">
                      <Calendar className="h-4 w-4 mr-1.5" />
                      Mensile Ricorrente
                    </span>
                    <span className="text-sm text-blue-800 dark:text-blue-300">
                      {formatEuro(getMonthlyTotal())}/mese
                    </span>
                  </div>
                </div>
              )}

              {/* One-time total for mobile - including monthly services with duration */}
              {hasOnetimeServices && (
                <div className="p-3 bg-gray-50 dark:bg-gray-800/50 font-medium">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-800 dark:text-gray-200 flex items-center">
                      <Tag className="h-4 w-4 mr-1.5" />
                      Una Tantum
                    </span>
                    <span className="text-sm text-gray-800 dark:text-gray-200">
                      {formatEuro(getOnetimeTotal())}
                    </span>
                  </div>
                </div>
              )}

              {/* Total row for mobile */}
              <div className="p-4 bg-black/10 dark:bg-white/10 font-medium">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold text-black dark:text-white flex items-center">
                    <DollarSign className="h-4 w-4 mr-1.5" />
                    Totale
                  </span>
                  <span className="text-sm font-bold text-black dark:text-white">
                    {formatEuro(getTotalAmount())}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
});